import { type Tela, temTela } from '@/features/auth/perfil-de-acesso';
import { secoesVisiveis } from '@/features/configuracoes/secoes';
import { cn } from '@/shared/lib/cn';
import { useAuthStore } from '@/shared/stores/auth-store';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  Bot,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Contact,
  FileChartColumn,
  FileSearch,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  Menu,
  MessageSquare,
  Radio,
  Settings,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

interface SimpleItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Tela do perfil de acesso que libera o item (ausente = todos). */
  tela?: Tela;
}

const DASHBOARD: SimpleItem = {
  to: '/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboard,
  tela: 'dashboard',
};
const ATENDIMENTO: SimpleItem = { to: '/atendimento', label: 'Atendimento', icon: MessageSquare };
const CONTATOS: SimpleItem = {
  to: '/contatos',
  label: 'Contatos',
  icon: Contact,
  tela: 'contatos',
};
const CAMPANHAS: SimpleItem = {
  to: '/campanhas',
  label: 'Campanhas',
  icon: Megaphone,
  tela: 'campanhas',
};

const RELATORIOS_ITEMS: Array<{ to: string; label: string; icon: LucideIcon; tela: Tela }> = [
  { to: '/consulta', label: 'Histórico de Chats', icon: FileSearch, tela: 'historico' },
  { to: '/agendamentos', label: 'Mensagens Agendadas', icon: CalendarClock, tela: 'agendadas' },
];

// Links diretos dentro de Configurações (rotas próprias).
const CONFIG_DIRECT: Array<{ to: string; label: string; icon: LucideIcon; tela: Tela }> = [
  { to: '/canais', label: 'Canais', icon: Radio, tela: 'canais' },
  { to: '/chatbot', label: 'Chatbot', icon: Bot, tela: 'chatbot' },
];

type MenuId = 'relatorios' | 'config' | 'compacto';

const PILULA =
  'inline-flex h-[36px] items-center gap-[7px] rounded-pill bg-brand-100 px-[14px] font-semibold text-[12px] text-brand-600';
const ICONE =
  'flex h-[38px] w-[38px] items-center justify-center rounded-md text-n-500 transition-colors hover:bg-n-100 hover:text-n-700';
const PAINEL = 'absolute z-50 rounded-[12px] border border-n-200 bg-n-0 p-[6px] shadow-md';
const LINHA =
  'flex h-[36px] w-full items-center gap-[10px] whitespace-nowrap rounded-[8px] px-[10px] text-left font-medium text-[13px] text-n-700 transition-colors hover:bg-n-100';

/**
 * Menu principal no TOPO (substitui o antigo Rail lateral). Cada item aparece
 * pra quem tem a tela no perfil de acesso (admin tem todas). Quem só tem o
 * Atendimento não vê menu nenhum.
 *
 * - Item ativo vira pílula com o nome; os demais ficam só ícone (nome no
 *   tooltip).
 * - Relatórios e Configurações abrem o dropdown SÓ NO CLIQUE (abrir no hover
 *   punha o tooltip em cima do painel). Fecha no clique de novo, clique fora,
 *   Esc ou troca de rota. Dentro de Configurações, os submenus das seções
 *   abrem no hover.
 * - Configurações: Canais e Chatbot como links diretos + as seções de
 *   SECOES_CONFIG (fonte única com a página) com submenu ao lado. É a única
 *   navegação das abas — a página de Configurações não tem mais menu lateral.
 * - `compacto` (janela estreita, vide Topbar): os ícones não cabem — antes o
 *   logo era espremido até sumir e o avatar (com o "Sair") era empurrado pra
 *   fora da tela. Vira UM botão de três barrinhas À ESQUERDA, antes do logo
 *   (dono, 18/out/2026), com a mesma lista escrita por extenso; Relatórios,
 *   Configurações e as seções abrem no clique, uma embaixo da outra (submenu
 *   ao lado não cabe e não existe hover no toque).
 */
export function TopNav({ compacto = false }: { compacto?: boolean }) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const pode = (tela: Tela | undefined): boolean => (tela ? temTela(user, tela) : true);
  const relatorios = RELATORIOS_ITEMS.filter((it) => pode(it.tela));
  const configDiretos = CONFIG_DIRECT.filter((it) => pode(it.tela));
  const podeConfiguracoes = pode('configuracoes');
  const secoesConfig = podeConfiguracoes ? secoesVisiveis(user?.profile === 'admin') : [];
  const temMenu =
    pode(DASHBOARD.tela) ||
    pode(CONTATOS.tela) ||
    pode(CAMPANHAS.tela) ||
    relatorios.length > 0 ||
    configDiretos.length > 0 ||
    podeConfiguracoes;

  const [open, setOpen] = useState<MenuId | null>(null);
  const [submenu, setSubmenu] = useState<string | null>(null);
  // Menu compacto: qual grupo de cima está aberto (Relatórios / Configurações).
  const [grupo, setGrupo] = useState<'relatorios' | 'config' | null>(null);
  // Wrapper (ícone + painel) do dropdown aberto — só um por vez.
  const abertoRef = useRef<HTMLDivElement>(null);

  const alternar = (id: MenuId): void => {
    setOpen((atual) => (atual === id ? null : id));
    setSubmenu(null);
  };
  const fecharJa = (): void => {
    setOpen(null);
    setSubmenu(null);
    setGrupo(null);
  };

  // Clique fora do menu aberto (inclusive no resto da barra do topo) / Esc
  // fecham; trocar de rota também. Clique no próprio ícone fica de fora — o
  // onClick dele alterna.
  // biome-ignore lint/correctness/useExhaustiveDependencies: registra 1x; os handlers só mexem em ref/setState
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (abertoRef.current && !abertoRef.current.contains(e.target as Node)) fecharJa();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') fecharJa();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: fecha ao mudar de rota
  useEffect(() => {
    fecharJa();
  }, [pathname]);

  const isActive = (p: string): boolean => pathname === p || pathname.startsWith(`${p}/`);
  const relatoriosActive = isActive('/consulta') || isActive('/agendamentos');
  const configActive = isActive('/configuracoes') || isActive('/canais') || isActive('/chatbot');

  const irParaAba = (tab: string): void => {
    // biome-ignore lint/suspicious/noExplicitAny: tipos de rota não registrados (padrão da codebase)
    void navigate({ to: '/configuracoes' as any, search: { tab } as any });
    fecharJa();
  };

  // Funções de render (não componentes internos): componente declarado dentro
  // do render ganha identidade nova a cada render e remonta o DOM — o estado
  // de hover/foco do dropdown se perderia ao abrir.
  function renderItem(item: SimpleItem) {
    if (!pode(item.tela)) return null;
    const Icon = item.icon;
    const active = isActive(item.to);
    return (
      <Link
        to={item.to}
        aria-label={item.label}
        // `data-cp-tip` direto (não `title`): o TooltipLayer move o `title`
        // pra cá no 1º hover, e aí o React não consegue mais apagar quando o
        // item vira a pílula ativa — o tooltip velho ficava preso nela.
        data-cp-tip={active ? undefined : item.label}
        aria-current={active ? 'page' : undefined}
        className={active ? PILULA : ICONE}
      >
        <Icon size={active ? 16 : 17} strokeWidth={2} aria-hidden="true" />
        {active && <span>{item.label}</span>}
      </Link>
    );
  }

  function renderDropdown(
    id: MenuId,
    label: string,
    Icon: LucideIcon,
    active: boolean,
    children: ReactNode,
  ) {
    const aberto = open === id;
    return (
      <div className="relative" ref={aberto ? abertoRef : undefined}>
        <button
          type="button"
          onClick={() => alternar(id)}
          aria-haspopup="menu"
          aria-expanded={aberto}
          aria-label={label}
          // Tooltip padrão (embaixo), como os outros ícones. Some com o menu
          // aberto — senão cobriria o painel — e na pílula ativa, que já
          // escreve o nome.
          data-cp-tip={active || aberto ? undefined : label}
          className={cn(active ? PILULA : ICONE, aberto && !active && 'bg-n-100 text-n-700')}
        >
          <Icon size={active ? 16 : 17} strokeWidth={2} aria-hidden="true" />
          {active && <span>{label}</span>}
        </button>
        {aberto && (
          <div role="menu" aria-label={label} className={cn(PAINEL, 'top-[calc(100%+6px)] left-0')}>
            {children}
          </div>
        )}
      </div>
    );
  }

  // Linha do menu compacto — o item da tela atual fica marcado.
  function renderLinhaCompacta(to: string, label: string, Icon: LucideIcon | null, recuo = false) {
    const active = isActive(to);
    return (
      <Link
        key={to}
        to={to}
        role="menuitem"
        aria-current={active ? 'page' : undefined}
        className={cn(LINHA, recuo && 'pl-[36px]', active && 'bg-brand-50 text-brand-600')}
      >
        {Icon && (
          <Icon
            size={16}
            strokeWidth={1.9}
            className={active ? 'text-brand-600' : 'text-n-500'}
            aria-hidden="true"
          />
        )}
        {label}
      </Link>
    );
  }

  // Cabeçalho de grupo do menu compacto (abre/fecha a lista embaixo dele).
  function renderGrupoCompacto(id: 'relatorios' | 'config', label: string, Icon: LucideIcon) {
    const aberto = grupo === id;
    return (
      <button
        type="button"
        aria-expanded={aberto}
        onClick={() => {
          setGrupo((g) => (g === id ? null : id));
          setSubmenu(null);
        }}
        className={cn(LINHA, aberto && 'bg-n-100')}
      >
        <Icon size={16} strokeWidth={1.9} className="text-n-500" aria-hidden="true" />
        {label}
        <ChevronDown
          size={15}
          strokeWidth={2.2}
          className={cn('ml-auto text-n-400 transition-transform', aberto && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
    );
  }

  // Quem só tem o Atendimento — um menu de um item só não serve pra nada,
  // então some. O espaçador mantém o resto do topo à direita (no compacto
  // quem põe o espaçador é o Topbar, depois do logo).
  if (!temMenu) return compacto ? null : <div className="flex-1" />;

  if (compacto) {
    const aberto = open === 'compacto';
    return (
      <>
        {/* -ml-[11px]: o desenho das barrinhas começa a 25px da borda (dono,
            18/out/2026). O topo tem 24px de respiro; o traço do ícone começa
            12px depois do início do botão de 38px → botão em 13px. */}
        <nav
          aria-label="Menu principal"
          className="relative -ml-[11px]"
          ref={aberto ? abertoRef : undefined}
        >
          <button
            type="button"
            onClick={() => alternar('compacto')}
            aria-haspopup="menu"
            aria-expanded={aberto}
            aria-label="Menu"
            data-cp-tip={aberto ? undefined : 'Menu'}
            className={cn(ICONE, aberto && 'bg-n-100 text-n-700')}
          >
            <Menu size={20} strokeWidth={2} aria-hidden="true" />
          </button>
          {aberto && (
            <div
              role="menu"
              aria-label="Menu principal"
              className={cn(
                PAINEL,
                'top-[calc(100%+6px)] left-0 max-h-[calc(100vh-80px)] min-w-[248px] overflow-y-auto',
              )}
            >
              {pode(DASHBOARD.tela) &&
                renderLinhaCompacta(DASHBOARD.to, DASHBOARD.label, DASHBOARD.icon)}
              {renderLinhaCompacta(ATENDIMENTO.to, ATENDIMENTO.label, ATENDIMENTO.icon)}
              {pode(CONTATOS.tela) &&
                renderLinhaCompacta(CONTATOS.to, CONTATOS.label, CONTATOS.icon)}

              {relatorios.length > 0 &&
                renderGrupoCompacto('relatorios', 'Relatórios', FileChartColumn)}
              {grupo === 'relatorios' &&
                relatorios.map((it) => renderLinhaCompacta(it.to, it.label, null, true))}

              {pode(CAMPANHAS.tela) &&
                renderLinhaCompacta(CAMPANHAS.to, CAMPANHAS.label, CAMPANHAS.icon)}

              {(configDiretos.length > 0 || podeConfiguracoes) &&
                renderGrupoCompacto('config', 'Configurações', Settings)}
              {grupo === 'config' && (
                <>
                  {configDiretos.map((it) => renderLinhaCompacta(it.to, it.label, null, true))}
                  {secoesConfig.map((g) => {
                    const unico = g.items.length === 1 ? g.items[0] : undefined;
                    if (unico) {
                      return (
                        <button
                          key={g.label}
                          type="button"
                          role="menuitem"
                          onClick={() => irParaAba(unico.id)}
                          className={cn(LINHA, 'pl-[36px]')}
                        >
                          {unico.label}
                        </button>
                      );
                    }
                    const secaoAberta = submenu === g.label;
                    return (
                      <div key={g.label}>
                        <button
                          type="button"
                          aria-expanded={secaoAberta}
                          onClick={() => setSubmenu((s) => (s === g.label ? null : g.label))}
                          className={cn(LINHA, 'pl-[36px]', secaoAberta && 'bg-n-100')}
                        >
                          {g.label}
                          <ChevronDown
                            size={15}
                            strokeWidth={2.2}
                            className={cn(
                              'ml-auto text-n-400 transition-transform',
                              secaoAberta && 'rotate-180',
                            )}
                            aria-hidden="true"
                          />
                        </button>
                        {secaoAberta &&
                          g.items.map((it) => (
                            <button
                              key={it.id}
                              type="button"
                              role="menuitem"
                              onClick={() => irParaAba(it.id)}
                              className={cn(LINHA, 'pl-[52px]')}
                            >
                              {it.label}
                            </button>
                          ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </nav>
      </>
    );
  }

  return (
    <nav aria-label="Menu principal" className="ml-[50px] flex flex-1 items-center gap-[10px]">
      {renderItem(DASHBOARD)}
      {renderItem(ATENDIMENTO)}
      {renderItem(CONTATOS)}

      {/* Relatórios — cada item pela tela dele no perfil. */}
      {relatorios.length > 0 &&
        renderDropdown(
          'relatorios',
          'Relatórios',
          FileChartColumn,
          relatoriosActive,
          <div className="min-w-[222px]">
            {relatorios.map((it) => {
              const Icon = it.icon;
              return (
                <Link key={it.to} to={it.to} role="menuitem" className={LINHA}>
                  <Icon size={16} strokeWidth={1.9} className="text-n-500" aria-hidden="true" />
                  {it.label}
                </Link>
              );
            })}
          </div>,
        )}

      {renderItem(CAMPANHAS)}

      {/* Configurações — Canais e Chatbot pela tela de cada um; as seções
          (abas da página) pela tela Configurações. */}
      {(configDiretos.length > 0 || podeConfiguracoes) &&
        renderDropdown(
          'config',
          'Configurações',
          Settings,
          configActive,
          <div className="min-w-[236px]">
            {configDiretos.map((it) => {
              const Icon = it.icon;
              return (
                <Link key={it.to} to={it.to} role="menuitem" className={LINHA}>
                  <Icon size={16} strokeWidth={1.9} className="text-n-500" aria-hidden="true" />
                  {it.label}
                </Link>
              );
            })}

            {configDiretos.length > 0 && podeConfiguracoes && (
              <div className="mx-[6px] my-[5px] h-px bg-n-100" />
            )}

            {secoesConfig.map((g) => {
              // Grupo de um item só (ex.: Conformidade → LGPD) vira link direto —
              // submenu com uma opção é clique à toa.
              const unico = g.items.length === 1 ? g.items[0] : undefined;
              return unico ? (
                <button
                  key={g.label}
                  type="button"
                  role="menuitem"
                  onClick={() => irParaAba(unico.id)}
                  className={LINHA}
                >
                  {unico.label}
                </button>
              ) : (
                <div
                  key={g.label}
                  className="relative"
                  onMouseEnter={() => setSubmenu(g.label)}
                  onMouseLeave={() => setSubmenu((s) => (s === g.label ? null : s))}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={submenu === g.label}
                    onClick={() => setSubmenu((s) => (s === g.label ? null : g.label))}
                    className={cn(LINHA, submenu === g.label && 'bg-n-100')}
                  >
                    {g.label}
                    <ChevronRight
                      size={15}
                      strokeWidth={2.2}
                      className="ml-auto text-n-400"
                      aria-hidden="true"
                    />
                  </button>
                  {submenu === g.label && (
                    <div
                      role="menu"
                      aria-label={g.label}
                      className={cn(PAINEL, '-top-[7px] left-[calc(100%+4px)] min-w-[220px]')}
                    >
                      {/* ponte invisível no vão de 4px até o submenu */}
                      <span
                        aria-hidden="true"
                        className="absolute top-0 bottom-0 -left-[8px] w-[8px]"
                      />
                      {g.items.map((it) => (
                        <button
                          key={it.id}
                          type="button"
                          role="menuitem"
                          onClick={() => irParaAba(it.id)}
                          className={LINHA}
                        >
                          {it.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>,
        )}
    </nav>
  );
}
